import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  Avatar,
  useTheme,
  alpha,
  Tooltip,
  TextField,
  IconButton,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import LoadingSpinner from './LoadingSpinner';
import {
  People as PeopleIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Payments as PaymentsIcon,
  Chat as ChatIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import api from '../services/api';
import InfoIcon from '@mui/icons-material/Info';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hasActionPermission } from '../utils/permissions';

const drawerWidth = 240;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

const Overview = () => {
  const theme = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purposeStats, setPurposeStats] = useState([]);
  const [purposeLoading, setPurposeLoading] = useState(true);
  const [purposeYear, setPurposeYear] = useState(new Date().getFullYear());
  const [purposeMonth, setPurposeMonth] = useState(new Date().getMonth() + 1);
  const [purposeCompare, setPurposeCompare] = useState('');
  const [marketingMetrics, setMarketingMetrics] = useState(null);
  const [purposeMetrics, setPurposeMetrics] = useState(null);
  const [period, setPeriod] = useState('month');
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [transactionLoading, setTransactionLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [compareWith, setCompareWith] = useState('');
  const [recentTransactionsLoading, setRecentTransactionsLoading] = useState(true);
  const [payscribeBalance, setPayscribeBalance] = useState(0);
  const [payscribeLoading, setPayscribeLoading] = useState(true);
  const [payscribeAccounts, setPayscribeAccounts] = useState([]);
  const [safeHavenBalance, setSafeHavenBalance] = useState(0);
  const [safeHavenLoading, setSafeHavenLoading] = useState(true);
  const [safeHavenAccounts, setSafeHavenAccounts] = useState([]);
  const [purposePeriod, setPurposePeriod] = useState('last_30_days');
  const [customRange, setCustomRange] = useState({ start: null, end: null });
  const [showSummaryInfo, setShowSummaryInfo] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminActivities, setAdminActivities] = useState([]);
  const [adminActivitiesLoading, setAdminActivitiesLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [userRole, setUserRole] = useState('admin');
  const [payscribeProfit, setPayscribeProfit] = useState(0);
  const [payscribeProfitLoading, setPayscribeProfitLoading] = useState(true);
  const [safeHavenProfit, setSafeHavenProfit] = useState(0);
  const [safeHavenProfitLoading, setSafeHavenProfitLoading] = useState(true);
  const [dataFilter, setDataFilter] = useState('current_month'); // 'all', 'current_month', 'month', 'year'
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  // Get user role and permissions from localStorage
  const [userPermissions, setUserPermissions] = useState([]);

  useEffect(() => {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        setUserRole(userData.type || 'admin');
        setUserPermissions(userData.permissions || []);
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
  }, []);

  // Generate years array (last 5 years) 
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  // Months array
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);

        // Determine month and year based on filter
        let monthToUse = null;
        let yearToUse = null;

        if (dataFilter === 'current_month') {
          const currentDate = new Date();
          monthToUse = currentDate.getMonth() + 1;
          yearToUse = currentDate.getFullYear();
        } else if (dataFilter === 'month') {
          monthToUse = filterMonth;
          yearToUse = filterYear;
        } else if (dataFilter === 'year') {
          yearToUse = filterYear;
          monthToUse = null; // For year filter, pass null for month
        }
        // If dataFilter === 'all', both monthToUse and yearToUse remain null

        const [
          users,
          balance,
          transactions,
          chats,
          recentUsersData,
          payscribeBalanceData,
          safeHavenBalanceData,
          totalSalesData,
          payscribeProfitData,
          safeHavenProfitData
        ] = await Promise.all([
          api.dashboard.getTotalUsers(monthToUse, yearToUse), // Filtered or all
          api.dashboard.getTotalBalance(), // Always total (no filter)
          api.dashboard.getTotalTransactions(monthToUse, yearToUse), // Filtered or all
          api.dashboard.getAllChats(monthToUse, yearToUse), // Filtered or all
          api.dashboard.getAllUsers(10), // Limit to 10 for dashboard display
          api.dashboard.getPayscribeBalance(), // Keep as is (no filter)
          api.dashboard.getSafeHavenBalance(), // Keep as is (no filter)
          api.dashboard.getTotalSales(monthToUse, yearToUse), // Filtered or all
          api.dashboard.getPayscribeProfit(monthToUse, yearToUse), // Filtered or all
          api.dashboard.getSafeHavenProfit(monthToUse, yearToUse) // Filtered or all
        ]);

        console.log('Balance response:', balance); // Debug log

        // Sort users by join date and get the 8 most recent
        const sortedUsers = recentUsersData
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 8);
        setRecentUsers(sortedUsers);

        setStats({
          totalUsers: users || 0,
          totalBalance: typeof balance === 'number' ? balance : 0,
          totalTransactions: transactions || 0,
          totalChats: chats?.length || 0,
          totalSales: totalSalesData || 0,
        });

        setPayscribeBalance(payscribeBalanceData?.balance || 0);
        setPayscribeAccounts(payscribeBalanceData?.accounts || []);

        setSafeHavenBalance(safeHavenBalanceData?.balance || 0);
        setSafeHavenAccounts(safeHavenBalanceData?.accounts || []);

        setPayscribeProfit(payscribeProfitData?.totalProfit || 0);
        setPayscribeProfitLoading(false);

        setSafeHavenProfit(safeHavenProfitData?.totalProfit || 0);
        setSafeHavenProfitLoading(false);

      } catch (error) {
        console.error('Error fetching stats:', error);
        // Set default values on error
        setStats({
          totalUsers: 0,
          totalBalance: 0,
          totalTransactions: 0,
          totalChats: 0,
          totalSales: 0,
        });
        setPayscribeProfit(0);
        setPayscribeProfitLoading(false);
        setSafeHavenProfit(0);
        setSafeHavenProfitLoading(false);
      } finally {
        setLoading(false);
        setPayscribeLoading(false);
        setSafeHavenLoading(false);
      }
    };

    fetchStats();
  }, [dataFilter, filterMonth, filterYear]);

  // Fetch transaction history separately
  useEffect(() => {
    const fetchTransactionHistory = async () => {
      setTransactionLoading(true);
      try {
        const data = await api.dashboard.getAllUsersTransactions(selectedYear, selectedMonth, compareWith);
        console.log('Raw transaction data:', data);
        setTransactionHistory(data);
      } catch (error) {
        console.error('Error fetching transaction history:', error);
      } finally {
        setTransactionLoading(false);
      }
    };

    fetchTransactionHistory();
  }, [selectedYear, selectedMonth, compareWith]);

  const fetchPurposeStats = async () => {
    setPurposeLoading(true);
    try {
      let params = {};
      if (purposePeriod === 'custom') {
        params.period = 'custom';
        params.startDate = customRange.start;
        params.endDate = customRange.end;
      } else {
        params.period = purposePeriod;
      }
      const data = await api.dashboard.getTransactionPurposeStats(params);
      setPurposeStats(data);

      // Only log activity if it's not the initial load
      if (!isInitialLoad) {
        await logAdminActivity(
          'VIEW_TRANSACTION_PURPOSES',
          `Viewed most common transaction purposes for period: ${purposePeriod}${purposePeriod === 'custom' ? ` (${customRange.start} to ${customRange.end})` : ''}`
        );
      }
    } catch (error) {
      setPurposeStats([]);
      console.error('Error fetching purpose stats:', error);
    } finally {
      setPurposeLoading(false);
    }
  };

  useEffect(() => {
    fetchPurposeStats();
    // Set isInitialLoad to false after first load
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [purposePeriod, customRange]);

  // Calculate marketing metrics
  useEffect(() => {
    if (transactionHistory.length > 0) {
      const currentPeriodData = transactionHistory.map(item => item.count);
      const comparePeriodData = transactionHistory.map(item => item.compareCount || 0);

      const calculateMetrics = (data) => ({
        total: data.reduce((sum, count) => sum + count, 0),
        average: data.reduce((sum, count) => sum + count, 0) / data.length,
        peak: Math.max(...data),
        peakDay: data.indexOf(Math.max(...data)) + 1,
        growth: comparePeriodData.length > 0
          ? ((data.reduce((sum, count) => sum + count, 0) - comparePeriodData.reduce((sum, count) => sum + count, 0)) /
            comparePeriodData.reduce((sum, count) => sum + count, 0)) * 100
          : 0
      });

      const currentMetrics = calculateMetrics(currentPeriodData);
      const compareMetrics = calculateMetrics(comparePeriodData);

      setMarketingMetrics({
        current: currentMetrics,
        compare: compareMetrics,
        trend: currentMetrics.growth > 0 ? 'up' : 'down'
      });
    }
  }, [transactionHistory]);

  // Calculate purpose metrics
  useEffect(() => {
    if (purposeStats.length > 0) {
      const totalTransactions = purposeStats.reduce((sum, item) => sum + item.count, 0);

      const processedStats = purposeStats.map(item => ({
        ...item,
        percentage: (item.count / totalTransactions) * 100,
        trend: item.previousCount ? ((item.count - item.previousCount) / item.previousCount) * 100 : 0
      }));

      setPurposeMetrics({
        total: totalTransactions,
        stats: processedStats,
        topPurpose: processedStats[0]?.purpose || 'N/A',
        topPercentage: processedStats[0]?.percentage || 0
      });
    }
  }, [purposeStats]);

  // Fetch recent transactions
  useEffect(() => {
    const fetchRecentTransactions = async () => {
      setRecentTransactionsLoading(true);
      try {
        const data = await api.dashboard.getAllTransactions({
          limit: 10,
          includeStats: false
        });

        // Handle both new response structure (object with transactions array) and potential fallback
        let transactions = [];
        if (data.transactions && Array.isArray(data.transactions)) {
          transactions = data.transactions;
        } else if (Array.isArray(data)) {
          transactions = data;
        } else if (data.data && Array.isArray(data.data)) {
          transactions = data.data;
        }

        // Sort by date and get the 9 most recent
        const sortedTransactions = transactions
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 9);

        setRecentTransactions(sortedTransactions);
      } catch (error) {
        console.error('Error fetching recent transactions:', error);
      } finally {
        setRecentTransactionsLoading(false);
      }
    };

    fetchRecentTransactions();
  }, []);

  const processTransactionHistory = (data) => {
    // Get the last 6 months
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(date.toLocaleString('default', { month: 'short' }));
    }

    // Initialize counts for each month
    const monthlyCounts = months.reduce((acc, month) => {
      acc[month] = 0;
      return acc;
    }, {});

    // Count transactions for each month
    data.forEach(transaction => {
      const date = new Date(transaction.createdAt);
      const month = date.toLocaleString('default', { month: 'short' });
      if (monthlyCounts.hasOwnProperty(month)) {
        monthlyCounts[month]++;
      }
    });

    return {
      labels: months,
      data: months.map(month => monthlyCounts[month])
    };
  };

  const StatCard = ({ title, value, icon, iconColor }) => (
    <Card
      sx={{
        borderRadius: 3,
        background: theme => `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
        boxShadow: theme => `
          0 4px 20px ${alpha(theme.palette.primary.main, 0.08)},
          0 2px 8px ${alpha(theme.palette.primary.main, 0.06)},
          0 0 0 1px ${alpha(theme.palette.primary.main, 0.03)}
        `,
        height: '100%',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme => `
            0 8px 24px ${alpha(theme.palette.primary.main, 0.12)},
            0 4px 12px ${alpha(theme.palette.primary.main, 0.08)},
            0 0 0 1px ${alpha(theme.palette.primary.main, 0.05)}
          `,
          '&::after': {
            transform: 'scale(1)',
            opacity: 1,
          },
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: theme => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%)`,
          transform: 'scale(0.8)',
          opacity: 0,
          transition: 'all 0.3s ease',
          zIndex: 0,
        },
      }}
    >
      <CardContent sx={{
        p: 3,
        position: 'relative',
        zIndex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          gap: 2
        }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: theme => `linear-gradient(135deg, ${alpha(iconColor || theme.palette.primary.main, 0.1)} 0%, ${alpha(iconColor || theme.palette.primary.main, 0.2)} 100%)`,
              color: iconColor || theme.palette.primary.main,
              fontSize: 24,
              boxShadow: theme => `0 4px 12px ${alpha(iconColor || theme.palette.primary.main, 0.15)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          >
            {icon}
          </Box>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{
              fontSize: '1rem',
              fontWeight: 500,
              letterSpacing: '0.5px'
            }}
          >
            {title}
          </Typography>
        </Box>
        <Typography
          variant="h4"
          fontWeight={700}
          color="primary"
          sx={{
            textShadow: theme => `0 2px 4px ${alpha(theme.palette.primary.main, 0.1)}`,
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            mt: 1
          }}
        >
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: theme.palette.text.primary,
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: {
            size: 12,
            weight: 'bold'
          }
        }
      },
      tooltip: {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.primary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            weight: 'bold'
          }
        },
        grid: {
          color: theme.palette.divider
        }
      },
      x: {
        ticks: {
          color: theme.palette.text.secondary,
          maxTicksLimit: 10,
          font: {
            weight: 'bold'
          }
        },
        grid: {
          color: theme.palette.divider
        }
      }
    },
  };

  // Transaction History Line Chart Data (revert to original)
  const transactionData = {
    labels: transactionHistory.map(item => {
      const date = new Date(selectedYear, selectedMonth - 1, item.day);
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    }) || [],
    datasets: [
      {
        label: 'Current Period',
        data: transactionHistory.map(item => item.count) || [],
        borderColor: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        tension: 0.4,
        fill: true,
        borderWidth: 2,
        pointBackgroundColor: theme.palette.primary.main,
        pointBorderColor: theme.palette.background.paper,
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      ...(compareWith ? [{
        label: 'Comparison Period',
        data: transactionHistory.map(item => item.compareCount) || [],
        borderColor: theme.palette.secondary.main,
        backgroundColor: alpha(theme.palette.secondary.main, 0.1),
        tension: 0.4,
        fill: true,
        borderWidth: 2,
        pointBackgroundColor: theme.palette.secondary.main,
        pointBorderColor: theme.palette.background.paper,
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      }] : [])
    ],
  };

  const transactionBarOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      legend: {
        ...chartOptions.plugins.legend,
        position: 'top',
      },
    },
    scales: {
      ...chartOptions.scales,
      x: {
        ...chartOptions.scales.x,
        stacked: false, // grouped, not stacked
      },
      y: {
        ...chartOptions.scales.y,
        stacked: false,
      },
    },
  };

  console.log('Processed chart data:', transactionData);

  const userData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'New Users',
        data: [12, 19, 3, 5, 2, 3],
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
      },
    ],
  };

  // Color palette for purposes
  const purposeColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
    theme.palette.primary.light,
    theme.palette.secondary.light,
    theme.palette.success.light,
  ];

  // Transform purposeStats for stacked bar chart
  const rawDays = [...new Set(purposeStats.map(item => item.day))].sort();
  const days = rawDays.map(dateStr => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });
  const purposes = [...new Set(purposeStats.map(item => item.purpose))];
  const purposeDatasets = purposes.map((purpose, idx) => ({
    label: purpose,
    data: rawDays.map(day => {
      const found = purposeStats.find(item => item.purpose === purpose && item.day === day);
      return found ? found.count : 0;
    }),
    backgroundColor: purposeColors[idx % purposeColors.length],
    borderRadius: 4,
  }));
  const purposeChartData = {
    labels: days,
    datasets: purposeDatasets,
  };

  const stackedChartOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      legend: {
        ...chartOptions.plugins.legend,
        position: 'top',
      },
    },
    scales: {
      ...chartOptions.scales,
      x: {
        ...chartOptions.scales.x,
        stacked: true,
      },
      y: {
        ...chartOptions.scales.y,
        stacked: true,
      },
    },
  };

  // Function to log admin activity
  const logAdminActivity = async (action, details) => {
    try {
      const activity = {
        action,
        details,
        timestamp: new Date().toISOString(),
        adminId: localStorage.getItem('adminId'), // Assuming you store admin ID in localStorage
        adminName: localStorage.getItem('adminName'), // Assuming you store admin name in localStorage
      };

      await api.dashboard.logAdminActivity(activity);
      // Refresh activities after logging
      fetchAdminActivities();
    } catch (error) {
      console.error('Error logging admin activity:', error);
    }
  };

  // Function to fetch admin activities
  const fetchAdminActivities = async () => {
    setAdminActivitiesLoading(true);
    try {
      const response = await api.dashboard.getAdminActivities();
      if (response && response.success) {
        // Sort activities by timestamp in descending order (most recent first)
        const sortedActivities = (response.data || []).sort((a, b) =>
          new Date(b.timestamp) - new Date(a.timestamp)
        );
        setAdminActivities(sortedActivities);
      } else {
        console.error('Failed to fetch admin activities:', response?.message);
        setAdminActivities([]);
      }
    } catch (error) {
      console.error('Error fetching admin activities:', error);
      setAdminActivities([]);
    } finally {
      setAdminActivitiesLoading(false);
    }
  };

  // Add useEffect to fetch admin activities
  useEffect(() => {
    fetchAdminActivities();
  }, []);

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
    logAdminActivity('VIEW_USER_DETAILS', `Viewed details for user: ${user.name || user.fullname}`);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const maskBvn = (bvn) => {
    if (!bvn || typeof bvn !== 'string' || bvn.length < 6) return bvn || '-';
    return bvn.slice(0, 2) + '*****' + bvn.slice(-4);
  };

  const getStatusColor = (status) => '#22c55e';

  const getBalanceColor = (balance) => {
    if (typeof balance !== 'number') return theme.palette.text.primary;
    if (balance > 10000) return theme.palette.success.main; // green
    if (balance >= 1000) return theme.palette.warning.main; // yellow
    return theme.palette.error.main; // red
  };

  const getActivityIcon = (action) => {
    switch (action) {
      case 'VIEW_USER_DETAILS':
        return <PeopleIcon fontSize="small" />;
      case 'UPDATE_USER':
        return <EditIcon fontSize="small" />;
      case 'DELETE_USER':
        return <DeleteIcon fontSize="small" />;
      case 'APPROVE_TRANSACTION':
        return <CheckCircleIcon fontSize="small" />;
      case 'REJECT_TRANSACTION':
        return <CancelIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  const getActivityColor = (action) => {
    switch (action) {
      case 'VIEW_USER_DETAILS':
        return theme.palette.info.main;
      case 'UPDATE_USER':
        return theme.palette.warning.main;
      case 'DELETE_USER':
        return theme.palette.error.main;
      case 'APPROVE_TRANSACTION':
        return theme.palette.success.main;
      case 'REJECT_TRANSACTION':
        return theme.palette.error.main;
      default:
        return theme.palette.primary.main;
    }
  };

  if (loading) {
    return <LoadingSpinner size="medium" />;
  }

  return (
    <Box
      component="main"
      sx={{
        bgcolor: theme.palette.background.default,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography
          variant="h5"
          fontWeight={700}
          color={theme.palette.primary.main}
        >
          Dashboard Overview
        </Typography>

        <Box sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          flexWrap: 'wrap',
          bgcolor: theme.palette.background.paper,
          p: 1.5,
          borderRadius: 2,
          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}>
          {/* Quick Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant={dataFilter === 'all' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => {
                setDataFilter('all');
              }}
              sx={{
                minWidth: 90,
                textTransform: 'none',
                borderRadius: 1.5,
                fontWeight: 600,
                ...(dataFilter === 'all' && {
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                })
              }}
            >
              All Time
            </Button>
            <Button
              variant={dataFilter === 'current_month' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => {
                setDataFilter('current_month');
                const currentDate = new Date();
                setFilterMonth(currentDate.getMonth() + 1);
                setFilterYear(currentDate.getFullYear());
              }}
              sx={{
                minWidth: 120,
                textTransform: 'none',
                borderRadius: 1.5,
                fontWeight: 600,
                ...(dataFilter === 'current_month' && {
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                })
              }}
            >
              This Month
            </Button>
            <Button
              variant={dataFilter === 'month' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setDataFilter('month')}
              sx={{
                minWidth: 110,
                textTransform: 'none',
                borderRadius: 1.5,
                fontWeight: 600,
                ...(dataFilter === 'month' && {
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                })
              }}
            >
              Select Month
            </Button>
            <Button
              variant={dataFilter === 'year' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setDataFilter('year')}
              sx={{
                minWidth: 100,
                textTransform: 'none',
                borderRadius: 1.5,
                fontWeight: 600,
                ...(dataFilter === 'year' && {
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                })
              }}
            >
              Select Year
            </Button>
          </Box>

          {/* Month/Year Selectors */}
          {dataFilter === 'month' && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 1, pl: 1, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
              <Select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                size="small"
                sx={{
                  minWidth: 140,
                  bgcolor: theme.palette.background.default,
                  borderRadius: 1.5,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.3)
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2
                  }
                }}
              >
                {months.map(month => (
                  <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                ))}
              </Select>
              <Select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                size="small"
                sx={{
                  minWidth: 100,
                  bgcolor: theme.palette.background.default,
                  borderRadius: 1.5,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.3)
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2
                  }
                }}
              >
                {years.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {dataFilter === 'year' && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 1, pl: 1, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
              <Select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                size="small"
                sx={{
                  minWidth: 100,
                  bgcolor: theme.palette.background.default,
                  borderRadius: 1.5,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.3)
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2
                  }
                }}
              >
                {years.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </Box>
          )}
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={(() => {
              if (dataFilter === 'all') return 'Total Users (All Time)';
              if (dataFilter === 'year') return `Total Users (${filterYear})`;
              if (dataFilter === 'month') return `Total Users (${months.find(m => m.value === filterMonth)?.label} ${filterYear})`;
              return 'Total Users (This Month)';
            })()}
            value={loading ? <LoadingSpinner size="small" /> : stats?.totalUsers || 0}
            icon={<PeopleIcon />}
            iconColor={theme.palette.primary.main}
          />
        </Grid>
        {hasActionPermission('view_financial_data', userPermissions, userRole) && (
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Customer Balance"
              value={loading ? <LoadingSpinner size="small" /> : `₦${Number(stats?.totalBalance || 0).toLocaleString()}`}
              icon={<AccountBalanceIcon />}
              iconColor={theme.palette.success.main}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={(() => {
              if (dataFilter === 'all') return 'Total Transactions (All Time)';
              if (dataFilter === 'year') return `Total Transactions (${filterYear})`;
              if (dataFilter === 'month') return `Total Transactions (${months.find(m => m.value === filterMonth)?.label} ${filterYear})`;
              return 'Total Transactions (This Month)';
            })()}
            value={loading ? <LoadingSpinner size="small" /> : stats?.totalTransactions || 0}
            icon={<ReceiptIcon />}
            iconColor={theme.palette.warning.main}
          />
        </Grid>
        {hasActionPermission('view_financial_data', userPermissions, userRole) && (
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title={(() => {
                if (dataFilter === 'all') return 'Total Deposit (All Time)';
                if (dataFilter === 'year') return `Total Deposit (${filterYear})`;
                if (dataFilter === 'month') return `Total Deposit (${months.find(m => m.value === filterMonth)?.label} ${filterYear})`;
                return 'Total Deposit (This Month)';
              })()}
              value={`₦${stats?.totalSales?.toLocaleString() || 0}`}
              icon={<PaymentsIcon />}
              iconColor="#10b981"
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={(() => {
              if (dataFilter === 'all') return 'Total Chats (All Time)';
              if (dataFilter === 'year') return `Total Chats (${filterYear})`;
              if (dataFilter === 'month') return `Total Chats (${months.find(m => m.value === filterMonth)?.label} ${filterYear})`;
              return 'Total Chats (This Month)';
            })()}
            value={stats?.totalChats || 0}
            icon={<ChatIcon />}
            iconColor="#a855f7"
          />
        </Grid>
        {hasActionPermission('view_financial_data', userPermissions, userRole) && (
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Payscribe Balance"
              value={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      color: payscribeBalance < 100000 ? theme.palette.error.main : theme.palette.text.primary,
                      animation: payscribeBalance < 100000 ? 'glow 2s ease-in-out infinite' : 'none',
                      '@keyframes glow': {
                        '0%': {
                          textShadow: '0 0 5px rgba(211, 47, 47, 0.5)',
                        },
                        '50%': {
                          textShadow: '0 0 20px rgba(211, 47, 47, 0.8)',
                        },
                        '100%': {
                          textShadow: '0 0 5px rgba(211, 47, 47, 0.5)',
                        },
                      },
                    }}
                  >
                    {payscribeLoading ? <LoadingSpinner size="small" /> : `₦${payscribeBalance?.toLocaleString() || 0}`}
                  </Typography>
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 1.5,
                            color: 'white',
                            fontWeight: 600,
                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                            pb: 1
                          }}
                        >
                          Bank Accounts
                        </Typography>
                        {payscribeAccounts.map((account) => (
                          <Box
                            key={`${account.bank}-${account.account}`}
                            sx={{
                              mb: 1.5,
                              p: 1,
                              borderRadius: 1,
                              background: 'rgba(255,255,255,0.05)',
                              '&:last-child': {
                                mb: 0
                              }
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: 'white',
                                mb: 0.5
                              }}
                            >
                              {account.bank}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'rgba(255,255,255,0.8)',
                                fontSize: '0.75rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <span>{account.account}</span>
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    }
                    arrow
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'rgba(0,0,0,0.9)',
                          backdropFilter: 'blur(8px)',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                          borderRadius: 2,
                          maxWidth: 300
                        }
                      },
                      arrow: {
                        sx: {
                          color: 'rgba(0,0,0,0.9)'
                        }
                      }
                    }}
                  >
                    <InfoIcon sx={{
                      fontSize: 16,
                      color: 'text.secondary',
                      cursor: 'help',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: 'primary.main',
                        transform: 'scale(1.1)'
                      }
                    }} />
                  </Tooltip>
                </Box>
              }
              icon={<WalletIcon />}
              iconColor={payscribeBalance < 100000 ? theme.palette.error.main : theme.palette.info.main}
            />
          </Grid>
        )}
        {hasActionPermission('view_financial_data', userPermissions, userRole) && (
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="SafeHaven Balance"
              value={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      color: safeHavenBalance < 1000000 ? 'error.main' : theme.palette.text.primary,
                      animation: safeHavenBalance < 1000000 ? 'glow 2s ease-in-out infinite' : 'none',
                      '@keyframes glow': {
                        '0%': {
                          textShadow: '0 0 5px rgba(211, 47, 47, 0.5)',
                        },
                        '50%': {
                          textShadow: '0 0 20px rgba(211, 47, 47, 0.8)',
                        },
                        '100%': {
                          textShadow: '0 0 5px rgba(211, 47, 47, 0.5)',
                        },
                      },
                    }}
                  >
                    {safeHavenLoading ? <LoadingSpinner size="small" /> : `₦${safeHavenBalance?.toLocaleString() || 0}`}
                  </Typography>
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 1.5,
                            color: 'white',
                            fontWeight: 600,
                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                            pb: 1
                          }}
                        >
                          SafeHaven Accounts
                        </Typography>
                        {safeHavenAccounts.map((account) => (
                          <Box
                            key={`${account.accountName}-${account.account}`}
                            sx={{
                              mb: 1.5,
                              p: 1,
                              borderRadius: 1,
                              background: 'rgba(255,255,255,0.05)',
                              '&:last-child': {
                                mb: 0
                              }
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: 'white',
                                mb: 0.5
                              }}
                            >
                              {account.accountName}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'rgba(255,255,255,0.8)',
                                fontSize: '0.75rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <span>{account.account}</span>
                              <span>•</span>
                              <span style={{ color: '#4CAF50' }}>₦{account.balance?.toLocaleString()}</span>
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    }
                    arrow
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'rgba(0,0,0,0.9)',
                          backdropFilter: 'blur(8px)',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                          borderRadius: 2,
                          maxWidth: 300
                        }
                      },
                      arrow: {
                        sx: {
                          color: 'rgba(0,0,0,0.9)'
                        }
                      }
                    }}
                  >
                    <InfoIcon sx={{
                      fontSize: 16,
                      color: 'text.secondary',
                      cursor: 'help',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: 'primary.main',
                        transform: 'scale(1.1)'
                      }
                    }} />
                  </Tooltip>
                </Box>
              }
              icon={<WalletIcon />}
              iconColor={safeHavenBalance < 1000000 ? theme.palette.error.main : theme.palette.success.main}
            />
          </Grid>
        )}
        {hasActionPermission('view_financial_data', userPermissions, userRole) && (
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title={(() => {
                if (dataFilter === 'all') return 'Payscribe Profit (All Time)';
                if (dataFilter === 'year') return `Payscribe Profit (${filterYear})`;
                if (dataFilter === 'month') return `Payscribe Profit (${months.find(m => m.value === filterMonth)?.label} ${filterYear})`;
                return 'Payscribe Profit (This Month)';
              })()}
              value={payscribeProfitLoading ? <LoadingSpinner size="small" /> : `₦${Number(payscribeProfit).toLocaleString()}`}
              icon={<TrendingUpIcon />}
              iconColor="#10b981"
            />
          </Grid>
        )}
        {hasActionPermission('view_financial_data', userPermissions, userRole) && (
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title={(() => {
                if (dataFilter === 'all') return 'SafeHaven Profit (All Time)';
                if (dataFilter === 'year') return `SafeHaven Profit (${filterYear})`;
                if (dataFilter === 'month') return `SafeHaven Profit (${months.find(m => m.value === filterMonth)?.label} ${filterYear})`;
                return 'SafeHaven Profit (This Month)';
              })()}
              value={safeHavenProfitLoading ? "Loading..." : `₦${safeHavenProfit.toLocaleString()}`}
              icon={<TrendingUpIcon />}
              iconColor="#10b981"
            />
          </Grid>
        )}
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{
            p: 3,
            borderRadius: 3,
            boxShadow: `0 2px 12px 0 ${alpha(theme.palette.primary.main, 0.08)}`,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: theme.palette.background.paper,
            mb: 3
          }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom color={theme.palette.text.primary}>
                  Most Common Transaction Purposes
                </Typography>
                {purposeMetrics && (
                  <Typography variant="body2" color={theme.palette.text.secondary}>
                    Total Transactions: {purposeMetrics.total} | Top Purpose: {purposeMetrics.topPurpose} ({purposeMetrics.topPercentage.toFixed(1)}%)
                  </Typography>
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="subtitle2" color={theme.palette.text.primary}>Filter by:</Typography>
                <Select
                  value={purposePeriod}
                  onChange={e => setPurposePeriod(e.target.value)}
                  size="small"
                  sx={{
                    minWidth: 100,
                    color: theme.palette.text.primary,
                    '& .MuiSelect-select': {
                      color: theme.palette.text.primary
                    }
                  }}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="this_week">This Week</MenuItem>
                  <MenuItem value="this_month">This Month</MenuItem>
                  <MenuItem value="this_year">This Year</MenuItem>
                  <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                  <MenuItem value="last_90_days">Last 90 Days</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
                {purposePeriod === 'custom' && (
                  <>
                    <TextField
                      type="date"
                      value={customRange.start || ''}
                      onChange={(e) => setCustomRange({ ...customRange, start: e.target.value })}
                      style={{ minWidth: 120, marginRight: 8 }}
                    />
                    <TextField
                      type="date"
                      value={customRange.end || ''}
                      onChange={(e) => setCustomRange({ ...customRange, end: e.target.value })}
                      style={{ minWidth: 120 }}
                    />
                  </>
                )}
              </Box>
            </Box>
            {purposeLoading ? (
              <LoadingSpinner size="small" />
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Box sx={{ height: 300 }}>
                    <Bar
                      data={purposeChartData}
                      options={stackedChartOptions}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ height: 300, overflowY: 'auto' }}>
                    {purposeMetrics?.stats && purposeMetrics.stats.length > 0 ? (
                      purposeMetrics.stats.map((stat) => (
                        <Paper
                          key={`${stat.purpose}-${stat.day}-${stat.count}-${stat.percentage}-${stat.trend}`}
                          sx={{
                            p: 2,
                            mb: 1,
                            borderRadius: 2,
                            borderLeft: '4px solid',
                            borderColor: purposeColors[purposes.indexOf(stat.purpose) % purposeColors.length],
                            bgcolor: theme.palette.background.paper,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.04)
                            }
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" fontWeight={600} color={theme.palette.text.primary}>
                              {stat.purpose}
                            </Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>
                              {stat.count} transactions
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                            <Typography variant="body2" color={theme.palette.text.secondary}>
                              {stat.percentage.toFixed(1)}% of total
                            </Typography>
                            {stat.trend !== 0 && (
                              <Typography
                                variant="body2"
                                color={stat.trend > 0 ? theme.palette.success.main : theme.palette.error.main}
                                fontWeight={600}
                              >
                                {stat.trend > 0 ? '↑' : '↓'} {Math.abs(stat.trend).toFixed(1)}%
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary" sx={{ mt: 2 }}>
                        No data available for the selected period.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>
          {/* Transaction History Chart */}
          <Paper sx={{
            p: 3,
            borderRadius: 3,
            boxShadow: `0 2px 12px 0 ${alpha(theme.palette.primary.main, 0.08)}`,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: theme.palette.background.paper,
            mb: 3
          }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={2} flexWrap="wrap">
              <Typography variant="subtitle1" color={theme.palette.text.primary}>Filter by:</Typography>
              <Select
                value={selectedYear}
                onChange={e => setSelectedYear(e.target.value)}
                size="small"
                sx={{
                  minWidth: 100,
                  color: theme.palette.text.primary,
                  '& .MuiSelect-select': {
                    color: theme.palette.text.primary
                  }
                }}
              >
                {years.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
              <Select
                value={selectedMonth}
                onChange={e => setSelectedMonth(e.target.value)}
                size="small"
                sx={{
                  minWidth: 120,
                  color: theme.palette.text.primary,
                  '& .MuiSelect-select': {
                    color: theme.palette.text.primary
                  }
                }}
              >
                {months.map(month => (
                  <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                ))}
              </Select>
              <Select
                value={compareWith}
                onChange={e => setCompareWith(e.target.value)}
                size="small"
                sx={{
                  minWidth: 150,
                  color: theme.palette.text.primary,
                  '& .MuiSelect-select': {
                    color: theme.palette.text.primary
                  }
                }}
                displayEmpty
              >
                <MenuItem value="">No Comparison</MenuItem>
                <MenuItem value="previous_day">Previous Day</MenuItem>
                <MenuItem value="previous_week">Previous Week</MenuItem>
                <MenuItem value="previous_month">Previous Month</MenuItem>
                <MenuItem value="previous_year">Previous Year</MenuItem>
              </Select>
            </Box>

            {/* Marketing Analytics Summary */}
            {!transactionLoading && marketingMetrics && (
              <Box sx={{
                mb: 3,
                p: 2,
                bgcolor: alpha(theme.palette.background.default, 0.6),
                borderRadius: 2
              }}>
                <Typography variant="h6" fontWeight={600} gutterBottom color={theme.palette.primary.main}>
                  Marketing Analytics Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper sx={{
                      p: 2,
                      bgcolor: theme.palette.background.paper,
                      borderRadius: 2,
                      boxShadow: `0 2px 8px 0 ${alpha(theme.palette.primary.main, 0.05)}`
                    }}>
                      <Typography variant="subtitle2" color={theme.palette.text.secondary}>Total Transactions</Typography>
                      <Typography variant="h6" fontWeight={600} color={theme.palette.text.primary}>
                        {marketingMetrics.current.total}
                        {marketingMetrics.compare && (
                          <Typography
                            component="span"
                            variant="body2"
                            color={marketingMetrics.trend === 'up' ? theme.palette.success.main : theme.palette.error.main}
                            sx={{ ml: 1 }}
                          >
                            ({marketingMetrics.trend === 'up' ? '↑' : '↓'} {Math.abs(marketingMetrics.current.growth).toFixed(1)}%)
                          </Typography>
                        )}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper sx={{
                      p: 2,
                      bgcolor: theme.palette.background.paper,
                      borderRadius: 2,
                      boxShadow: `0 2px 8px 0 ${alpha(theme.palette.primary.main, 0.05)}`
                    }}>
                      <Typography variant="subtitle2" color={theme.palette.text.secondary}>Average Daily Transactions</Typography>
                      <Typography variant="h6" fontWeight={600} color={theme.palette.text.primary}>
                        {marketingMetrics.current.average.toFixed(1)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper sx={{
                      p: 2,
                      bgcolor: theme.palette.background.paper,
                      borderRadius: 2,
                      boxShadow: `0 2px 8px 0 ${alpha(theme.palette.primary.main, 0.05)}`
                    }}>
                      <Typography variant="subtitle2" color={theme.palette.text.secondary}>Peak Day</Typography>
                      <Typography variant="h6" fontWeight={600} color={theme.palette.text.primary}>
                        Day {marketingMetrics.current.peakDay} ({marketingMetrics.current.peak} transactions)
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
                {compareWith && (
                  <Box sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 2,
                    boxShadow: `0 2px 8px 0 ${alpha(theme.palette.primary.main, 0.05)}`
                  }}>
                    <Typography variant="subtitle2" color={theme.palette.text.secondary} gutterBottom>
                      Period Comparison
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color={theme.palette.text.primary}>
                          Current Period: {marketingMetrics.current.total} transactions
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color={theme.palette.text.primary}>
                          Previous Period: {marketingMetrics.compare.total} transactions
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          color={marketingMetrics.trend === 'up' ? theme.palette.success.main : theme.palette.error.main}
                          fontWeight={600}
                        >
                          {marketingMetrics.trend === 'up' ? 'Growth' : 'Decline'}: {Math.abs(marketingMetrics.current.growth).toFixed(1)}%
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
            )}

            <Typography variant="h6" fontWeight={600} gutterBottom color={theme.palette.text.primary}>
              Transaction History - {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
            </Typography>
            {transactionLoading ? (
              <LoadingSpinner size="small" />
            ) : transactionHistory.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography color={theme.palette.text.secondary}>No transaction data available for the selected period</Typography>
              </Box>
            ) : (
              <Box sx={{ height: 300 }}>
                <Line data={transactionData} options={chartOptions} />
              </Box>
            )}
          </Paper>
          {/* Recent Transactions */}
          <Paper sx={{
            p: 3,
            borderRadius: 3,
            boxShadow: `0 2px 12px 0 ${alpha(theme.palette.primary.main, 0.08)}`,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: theme.palette.background.paper
          }}>
            <Typography variant="h6" fontWeight={600} gutterBottom color={theme.palette.text.primary}>
              Recent Transactions
            </Typography>
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {recentTransactionsLoading ? (
                <LoadingSpinner size="small" />
              ) : recentTransactions.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <Typography color={theme.palette.text.secondary}>No recent transactions</Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {recentTransactions.map((transaction) => (
                    <Grid item xs={12} sm={6} md={4} key={`${transaction._id}-${transaction.type}-${transaction.createdAt}-${transaction.amount}`}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 2,
                          borderRadius: 2,
                          bgcolor: theme.palette.background.paper,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          height: '100%',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.04)
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2,
                              bgcolor: transaction.type === 'credit' ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                              color: transaction.type === 'credit' ? theme.palette.success.main : theme.palette.error.main,
                            }}
                          >
                            {transaction.type === 'credit' ? '↑' : '↓'}
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600} color={theme.palette.text.primary}>
                              {transaction.purpose || 'Transaction'}
                            </Typography>
                            <Typography variant="caption" color={theme.palette.text.secondary}>
                              {new Date(transaction.createdAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            color={transaction.type === 'credit' ? theme.palette.success.main : theme.palette.error.main}
                          >
                            {transaction.type === 'credit' ? '+' : '-'}₦{transaction.amount?.toLocaleString() || '0'}
                          </Typography>
                          <Typography variant="caption" color={theme.palette.text.secondary}>
                            {transaction.type === 'credit' ? 'Credit' : 'Debit'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Transaction Summary */}
          <Paper sx={{
            p: 3,
            borderRadius: 3,
            boxShadow: `0 2px 12px 0 ${alpha(theme.palette.primary.main, 0.08)}`,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: theme.palette.background.paper,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 400
          }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6" fontWeight={600} gutterBottom color={theme.palette.text.primary}>
                Transaction Summary
              </Typography>
              <IconButton size="small" onClick={() => setShowSummaryInfo((prev) => !prev)}>
                <InfoIcon color={showSummaryInfo ? 'primary' : 'action'} />
              </IconButton>
            </Box>
            {showSummaryInfo && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <b>About Transaction Summary:</b> This section gives you, as an admin, a quick overview of how money is moving through your platform right now.<br />
                <br />
                <b>Total Credits</b> shows all recent transactions where money was added to user accounts (like deposits or incoming transfers).<br />
                <b>Total Debits</b> shows all recent transactions where money was taken out (like bill payments or outgoing transfers).<br />
                <b>Net Flow</b> is credits minus debits. If it's positive, more money came in than went out. If it's negative, more money left the platform than came in.<br />
                <br />
                <b>Example:</b> If the last few transactions are:<br />
                - User A deposited ₦5,000 (credit)<br />
                - User B paid a bill of ₦2,000 (debit)<br />
                - User C received a transfer of ₦3,000 (credit)<br />
                - User D withdrew ₦1,000 (debit)<br />
                <br />
                Your summary would show:<br />
                <b>Total Credits:</b> ₦8,000 (₦5,000 + ₦3,000)<br />
                <b>Total Debits:</b> ₦3,000 (₦2,000 + ₦1,000)<br />
                <b>Net Flow:</b> ₦5,000 (₦8,000 - ₦3,000)<br />
                <br />
                This helps you spot trends, catch unusual activity, and keep your platform's finances healthy—all at a glance.
              </Typography>
            )}
            <Box>
              {recentTransactionsLoading ? (
                <LoadingSpinner size="small" />
              ) : (
                <Box sx={{ p: 2 }}>
                  <Box sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.background.default, 0.6),
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}>
                    <Typography variant="subtitle2" color={theme.palette.text.secondary} gutterBottom>
                      Total Credits
                    </Typography>
                    <Typography variant="h6" color={theme.palette.success.main} fontWeight={600}>
                      ₦{recentTransactions
                        .filter(t => t.type === 'credit')
                        .reduce((sum, t) => sum + (t.amount || 0), 0)
                        .toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.background.default, 0.6),
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}>
                    <Typography variant="subtitle2" color={theme.palette.text.secondary} gutterBottom>
                      Total Debits
                    </Typography>
                    <Typography variant="h6" color={theme.palette.error.main} fontWeight={600}>
                      ₦{recentTransactions
                        .filter(t => t.type === 'debit')
                        .reduce((sum, t) => sum + (t.amount || 0), 0)
                        .toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.background.default, 0.6),
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}>
                    <Typography variant="subtitle2" color={theme.palette.text.secondary} gutterBottom>
                      Net Flow
                    </Typography>
                    <Typography
                      variant="h6"
                      color={recentTransactions.reduce((sum, t) =>
                        sum + (t.type === 'credit' ? t.amount : -t.amount), 0) >= 0 ? theme.palette.success.main : theme.palette.error.main}
                      fontWeight={600}
                    >
                      {recentTransactions.reduce((sum, t) =>
                        sum + (t.type === 'credit' ? t.amount : -t.amount), 0) >= 0 ? '+' : '-'}
                      ₦{Math.abs(recentTransactions.reduce((sum, t) =>
                        sum + (t.type === 'credit' ? t.amount : -t.amount), 0)).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Recent Users */}
          <Paper sx={{
            p: 3,
            borderRadius: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: theme => `linear-gradient(90deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 600,
            mt: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PeopleIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
              <Typography variant="h6" fontWeight={600} color={theme.palette.text.primary}>
                Recent Users
              </Typography>
            </Box>
            <Box sx={{
              maxHeight: 510,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: alpha(theme.palette.primary.main, 0.05),
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: alpha(theme.palette.primary.main, 0.2),
                borderRadius: '4px',
                '&:hover': {
                  background: alpha(theme.palette.primary.main, 0.3),
                },
              },
            }}>
              {recentUsers.map((user) => (
                <Card
                  key={`${user._id || user.id}-${user.name || user.fullname}-${user.createdAt}`}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    background: theme.palette.background.paper,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.05)}`,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                    },
                  }}
                  onClick={() => handleUserClick(user)}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          src={user.profilePic || ''}
                          alt={user.name || user.fullname || 'User'}
                          sx={{
                            width: 45,
                            height: 45,
                            mr: 2,
                            border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                          }}
                        >
                          {!(user.profilePic) && (user.name?.[0] || user.fullname?.[0] || '?')}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600} color={theme.palette.text.primary}>
                            {user.name || user.fullname || '-'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <span>Joined</span>
                            <span>{new Date(user.createdAt).toLocaleDateString()}</span>
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{
                        textAlign: 'right',
                        p: 1,
                        borderRadius: 1,
                        background: alpha(theme.palette.primary.main, 0.05),
                      }}>
                        <Typography variant="subtitle2" fontWeight={600} color={theme.palette.primary.main}>
                          ₦{user.balance?.toLocaleString() || '0'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            display: 'block',
                            mt: 0.5,
                          }}
                        >
                          Balance
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Paper>

          {/* Admin Activities */}
          {userRole === 'superadmin' && (
            <Paper sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              background: theme => `linear-gradient(90deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 400,
              mt: 3
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                  <Typography variant="h6" fontWeight={600} color={theme.palette.text.primary}>
                    Admin Activities
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={fetchAdminActivities}
                  sx={{
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1)
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Box>
              {adminActivitiesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <LoadingSpinner size="small" />
                </Box>
              ) : adminActivities.length === 0 ? (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 200,
                  color: theme.palette.text.secondary
                }}>
                  <Typography>No activities found</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Action</TableCell>
                        <TableCell>Details</TableCell>
                        <TableCell>Admin</TableCell>
                        <TableCell>Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {adminActivities
                        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                        .slice(0, 3)
                        .map((activity, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.04)
                              }
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    color: getActivityColor(activity.action),
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  {getActivityIcon(activity.action)}
                                </Box>
                                <Typography variant="body2">
                                  {activity.action.replace(/_/g, ' ')}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {activity.details}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {activity.adminName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip
                                title={new Date(activity.timestamp).toLocaleString()}
                                arrow
                                placement="top"
                              >
                                <Typography variant="body2" color="text.secondary">
                                  {new Date(activity.timestamp).toLocaleDateString()}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* User Details Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          User Details
          <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedUser && (
            <>
              <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                <Avatar
                  src={selectedUser.profilePic || ''}
                  alt={selectedUser.name || selectedUser.fullname || 'User'}
                  sx={{ width: 80, height: 80, mb: 1 }}
                >
                  {!(selectedUser.profilePic) && (selectedUser.name?.[0] || selectedUser.fullname?.[0] || '?')}
                </Avatar>
                <Typography variant="h6" fontWeight={700}>
                  {selectedUser.name || selectedUser.fullname || '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedUser.email}
                </Typography>
              </Box>
              {[
                { label: 'Username', value: selectedUser.username || '-' },
                { label: 'Phone', value: selectedUser.phone || '-' },
                { label: 'Gender', value: selectedUser.gender || '-' },
                {
                  label: 'Balance', value: (
                    <strong style={{ color: getBalanceColor(Number(selectedUser.balance)) }}>
                      {selectedUser.balance !== undefined ? `₦${Number(selectedUser.balance).toLocaleString()}` : '-'}
                    </strong>
                  )
                },
                { label: 'Role', value: selectedUser.role || '-' },
                {
                  label: 'Status', value: (
                    <Box
                      component="span"
                      sx={{
                        backgroundColor: getStatusColor(selectedUser.status),
                        color: '#fff',
                        px: 2,
                        py: 0.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: 13,
                        display: 'inline-block',
                        minWidth: 70,
                        textAlign: 'center',
                      }}
                    >
                      {selectedUser.status || 'Active'}
                    </Box>
                  )
                },
                { label: 'Joined', value: selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : '-' },
                { label: 'NIN', value: selectedUser.nin || selectedUser.vninNo || '-' },
                { label: 'BVN', value: maskBvn(selectedUser.bvn || selectedUser.vbvnNo || '') },
                { label: 'isVerified', value: selectedUser.isVerified ? 'Yes' : 'No' },
                { label: 'isKycVerified', value: selectedUser.isKycVerified ? 'Yes' : 'No' },
                { label: 'isKycninVerified', value: selectedUser.isKycninVerified ? 'Yes' : 'No' },
                { label: 'isKycbvnVerified', value: selectedUser.isKycbvnVerified ? 'Yes' : 'No' },
                { label: 'Virtual Account Bank', value: selectedUser.virtuaAccountBank || '-' },
                { label: 'Virtual Account Name', value: selectedUser.virtuaAccountName || '-' },
                { label: 'Virtual Account No', value: selectedUser.virtuaAccountNo || '-' },
              ].map((item) => (
                <Box key={item.label} display="flex" justifyContent="space-between" alignItems="center" py={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Overview; 